import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { useLang } from "@/context/LangContext";

const defaultFaqCategories = [
  {
    category: "General",
    questions: [
      { q: "What is GACA International?", a: "GACA International (Global Assurance & Certification Authority) is a certification and cybersecurity assurance organization. We deliver ISO certification services in partnership with internationally accredited certification bodies, backed by deep cybersecurity expertise." },
      { q: "Is GACA an accredited certification body?", a: "GACA International delivers certification services in partnership with internationally accredited certification bodies. We are not ourselves an accredited certification body — all certifications are issued through our accredited partners who are recognized under the IAF Multilateral Recognition Arrangement." },
      { q: "Which standards does GACA certify against?", a: "We offer certification services for ISO 27001, ISO 9001, ISO 22301, ISO 27701, ISO 14001, ISO 45001, ISO 37001, and support readiness assessments for NIST CSF and SOC 2." },
    ],
  },
  {
    category: "Certification Process",
    questions: [
      { q: "How long does the certification process take?", a: "The typical time to certification readiness is approximately 90 days, depending on your organization's size, complexity, and current state of compliance. The actual certification audit is typically completed within 1-2 weeks." },
      { q: "What is the difference between Stage 1 and Stage 2 audits?", a: "Stage 1 is a documentation review that verifies your management system design meets the standard's requirements. Stage 2 is a comprehensive on-site audit evaluating the implementation and effectiveness of your management system, including staff interviews and evidence gathering." },
      { q: "How long is a certification valid?", a: "ISO management system certifications are typically valid for 3 years, subject to successful annual surveillance audits. A full recertification audit is conducted before the certificate expires." },
      { q: "Can we maintain our existing certification?", a: "Yes, if you are transitioning from another certification body, we can facilitate the transfer process. Your existing certification status is maintained throughout the transition." },
    ],
  },
  {
    category: "Costs & Engagement",
    questions: [
      { q: "How much does ISO certification cost?", a: "Certification costs vary based on the standard, your organization's size, number of locations, and complexity of operations. Contact us for a customized quotation tailored to your specific requirements." },
      { q: "Do you offer bundled certification packages?", a: "Yes, we offer integrated management system (IMS) audits that combine multiple standards (e.g., ISO 27001 + ISO 9001) into a single audit engagement, reducing time and cost." },
      { q: "What is included in the certification fee?", a: "Our certification engagement typically includes gap analysis, pre-audit consultation, Stage 1 and Stage 2 audits, certification issuance through our accredited partner, and ongoing surveillance audit support." },
    ],
  },
  {
    category: "Training",
    questions: [
      { q: "What training courses do you offer?", a: "We offer Lead Auditor training (ISO 27001, ISO 9001), Lead Implementer training (ISO 22301), Cybersecurity Fundamentals, Risk Management Workshops, and Security Awareness programs for employees." },
      { q: "Are your training courses accredited?", a: "Our training programs are designed and delivered by certified lead auditors and subject matter experts. Participants receive certificates of completion recognized by the industry." },
      { q: "Do you offer customized corporate training?", a: "Yes, we design tailored training programs based on your organization's specific needs, industry, and compliance objectives. Both on-site and virtual delivery options are available." },
    ],
  },
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#0B2545]/10 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-question-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
        className="w-full flex items-center justify-between py-5 px-6 text-left hover:bg-[#F5F7FA] transition-colors"
      >
        <span className="text-sm font-medium text-[#0B2545] pr-4">{question}</span>
        {open ? <ChevronUp size={18} className="text-[#13A8BD] flex-shrink-0" /> : <ChevronDown size={18} className="text-slate-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-6 pb-5 animate-fade-in">
          <p className="text-sm text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const { t } = useLang();
  const fp = t?.faq_page || {};
  const tCategories = fp.categories || [];

  const faqCategories = defaultFaqCategories.map((def, ci) => {
    const tc = tCategories[ci];
    return {
      category: tc?.category || def.category,
      questions: def.questions.map((dq, qi) => {
        const tq = tc?.questions?.[qi];
        return { q: tq?.q || dq.q, a: tq?.a || dq.a };
      }),
    };
  });

  return (
    <div className="page-transition pt-20" data-testid="faq-page">
      <section className="bg-[#0B2545] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">{fp.badge || "FAQ"}</p>
          <h1 data-testid="faq-page-heading" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.1] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
            {fp.title || "Frequently Asked Questions"}
          </h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
            {fp.subtitle || "Find answers to common questions about our certification services, processes, and training programs."}
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="space-y-10">
            {faqCategories.map((cat) => (
              <div key={cat.category}>
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">{cat.category}</h2>
                <div className="border border-[#0B2545]/10">
                  {cat.questions.map((q) => (
                    <FAQItem key={q.q} question={q.q} answer={q.a} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F5F7FA] border-t border-[#0B2545]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-2xl font-bold text-[#0B2545] mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
            {fp.cta_title || "Still Have Questions?"}
          </h2>
          <p className="text-sm text-slate-600 mb-6">
            {fp.cta_desc || "Our team is ready to help. Reach out to us for any further inquiries."}
          </p>
          <Link to="/contact" data-testid="faq-cta" className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors">
            {fp.cta_button || "Contact Us"} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
